import React, { useState } from 'react';
import { Search, AlertCircle, CheckCircle, Globe, Database, Brain, Settings } from 'lucide-react';
import ProcessingButton from './ProcessingButton';

const MultiNewsInput = ({ 
  onCollectNews, 
  isCollecting, 
  collectionResults 
}) => {
  const [strategy, setStrategy] = useState('comprehensive');
  const [maxArticles, setMaxArticles] = useState(50);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [storeToDatabase, setStoreToDatabase] = useState(true);
  const [autoProcess, setAutoProcess] = useState(false);
  const [selectedApis, setSelectedApis] = useState(['gnews', 'mediastack', 'newsdata', 'worldnews']);

  const strategies = [
    { value: 'comprehensive', label: 'Comprehensive', description: 'Search across all categories and APIs' },
    { value: 'pestle_focused', label: 'PESTLE Focused', description: 'Focus on PESTLE framework analysis' },
    { value: 'india_focused', label: 'India Focused', description: 'India-specific news and policies' },
    { value: 'mineral_focused', label: 'Mineral Focused', description: 'Critical minerals and mining focus' }
  ];

  const availableApis = [
    { value: 'gnews', label: 'GNews', description: 'Global news coverage' },
    { value: 'mediastack', label: 'MediaStack', description: 'High-quality news sources' },
    { value: 'newsdata', label: 'NewsData.io', description: 'Real-time news data' },
    { value: 'worldnews', label: 'WorldNews', description: 'International news coverage' }
  ];

  const handleApiToggle = (apiValue) => {
    setSelectedApis(prev => 
      prev.includes(apiValue) 
        ? prev.filter(api => api !== apiValue)
        : [...prev, apiValue]
    );
  };

  const handleCollect = () => {
    const requestData = {
      strategy,
      max_articles_per_api: maxArticles,
      from_date: fromDate || null,
      to_date: toDate || null,
      store_to_database: storeToDatabase,
      auto_process: autoProcess,
      apis: selectedApis.length > 0 ? selectedApis : null
    };
    
    onCollectNews(requestData);
  };

  const getDateRange = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
    
    return {
      today: today.toISOString().split('T')[0],
      thirtyDaysAgo: thirtyDaysAgo.toISOString().split('T')[0]
    };
  };

  const { today, thirtyDaysAgo } = getDateRange();

  return (
    <div className="space-y-6">
      {/* Strategy Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Collection Strategy
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {strategies.map((strat) => (
            <label key={strat.value} className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="strategy"
                value={strat.value}
                checked={strategy === strat.value}
                onChange={(e) => setStrategy(e.target.value)}
                className="mt-1 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">{strat.label}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{strat.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* API Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          News APIs to Use
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {availableApis.map((api) => (
            <label key={api.value} className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedApis.includes(api.value)}
                onChange={() => handleApiToggle(api.value)}
                className="mt-1 text-blue-600 focus:ring-blue-500 rounded"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">{api.label}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{api.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Collection Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Max Articles per API
          </label>
          <select
            value={maxArticles}
            onChange={(e) => setMaxArticles(parseInt(e.target.value))}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value={25}>25 articles</option>
            <option value={50}>50 articles</option>
            <option value={100}>100 articles</option>
            <option value={200}>200 articles</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Date Range (Optional)
          </label>
          <div className="space-y-2">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={today}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="From date"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              max={today}
              min={fromDate}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="To date"
            />
          </div>
        </div>
      </div>

      {/* Processing Options */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Processing Options
        </label>
        
        <div className="space-y-2">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={storeToDatabase}
              onChange={(e) => setStoreToDatabase(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <Database className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Store articles to database
            </span>
          </label>
          
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoProcess}
              onChange={(e) => setAutoProcess(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <Brain className="w-4 h-4 text-purple-600" />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Auto-process with PESTLE analysis
            </span>
          </label>
        </div>
      </div>

      {/* Information Panel */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-2">Collection Strategy Details:</p>
            <ul className="space-y-1 text-xs">
              <li>• <strong>Comprehensive:</strong> Uses all APIs with broad keyword coverage</li>
              <li>• <strong>PESTLE Focused:</strong> Targets political, economic, social, technological, legal, and environmental factors</li>
              <li>• <strong>India Focused:</strong> Emphasizes India-specific policies and developments</li>
              <li>• <strong>Mineral Focused:</strong> Concentrates on critical minerals and mining industry</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Collection Button */}
      <ProcessingButton
        onClick={handleCollect}
        disabled={isCollecting || selectedApis.length === 0}
        isLoading={isCollecting}
        loadingText="Collecting news from multiple APIs..."
        icon={Search}
        variant="primary"
        className="w-full"
      >
        Collect News from Multiple APIs
      </ProcessingButton>

      {/* Collection Results */}
      {collectionResults && (
        <div className={`p-4 rounded-lg border ${
          collectionResults.success 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-start gap-3">
            {collectionResults.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            )}
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-white mb-2">
                {collectionResults.success ? 'Collection Successful' : 'Collection Failed'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                {collectionResults.success ? (
                  <>
                    <p>Articles collected: {collectionResults.articles_collected || 0}</p>
                    <p>Articles stored: {collectionResults.articles_stored || 0}</p>
                    <p>APIs used: {collectionResults.apis_used?.join(', ') || 'N/A'}</p>
                    <p>Strategy: {collectionResults.strategy_used || 'N/A'}</p>
                  </>
                ) : (
                  <p>Error: {collectionResults.error || 'Unknown error occurred'}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiNewsInput;
